import React, { Suspense, lazy, useEffect } from 'react';
import {
	BrowserRouter as Router,
	Switch,
	Route,
	Redirect,
} from 'react-router-dom';
import AppSetup from './AppSetup';
import '../../styles/sb-admin-2.css';
import '../../styles/grid.scss';
import './FullApp.scss';
import { KYCStatus } from '../../interface';
import RootModal from '../../RootModal';
import RootSideBar from '../../RootSideBar';
import * as urls from '../../urls';
import { EmptyState, Loader } from '@kaleyra/emmy';
import {
	checkPermissions,
	checkPrivileges,
	checkChatbotPrivileges,
	redirectToDashboard,
	checkEnabledChannel,
	checkForSpecificCompanyId,
	checkWhatsappOnlyRole,
	defaultRedirectTo,
	checkIsConversationEnabled,
	checkViewOnlyPrivileges,
	checkFailoverAccess,
	isActiveWhatsappAccount
} from '../../utils';
import {
	campaignsUrls,
	voiceUrls,
	developersUrl,
	channelsUrls,
	whatsappCampaignUrls,
	integrationDirectoryUrl,
	smsUrls,
	receiveMessageUrl,
	dashboardUrls,
	conversationUrls,
	fallbackUrl,
	whatsappCampaignLaunchUrls,
	voiceCampaignLaunchUrls
} from '../../../src/constants';

import { TabItemPropsType } from '@kaleyra/emmy/dist/atoms/TabItem';
// import KycHoc from '../../hoc/kycRedirect.hoc';
import { REDIRECT_URL, HIDE_FEATURE_URLS, IS_SECONDARY_REGION } from '../../base';
import SipCredential from '../Campaigns/SipUserGroup/Credential/containers/Credential';
import SipIP from '../Campaigns/SipIP/containers/index';
import { SidebarMenuProps } from '../../components/common/KDSLeftSideBar/KDSLeftSideBar';
import { useSelector } from 'react-redux';
import { IStateType } from '../../store/models/root.interface';
import fallbackDashboard from '../AnalyticsBeta/Dashboard/fallback/container/fallbackDashboard';
import { useTranslation } from 'react-i18next';

const PageContent = lazy(() => import('../../components/common/PageContent'));

const ErrorComponent = lazy(() =>
	import('../../components/ErrorMessage/Error')
);
const AddingSubAccounts = lazy(() =>
	import('../SubAccountsManagement/SubAccounts/containers/addingSubaccount')
);
const EditSubAccount = lazy(() =>
	import('../SubAccountsManagement/SubAccounts/containers/editSubaccount')
);
const subAccountsManagementTab = lazy(() =>
	import('../SubAccountsManagement/SubAccountsManagement')
);
const CampaignRoute = lazy(() => import('../Campaigns/Voice/CampaignRoute'));

const ChannelsList = lazy(() => import('../channels/channelsList'));
// const ChannelsNav = lazy(() => import('../ChannelSettings/ChannelsNav'));
const Chatbot = lazy(() => import('../Chatbot/Chatbot'));
const ChatbotList = lazy(() =>
	import('../ChatbotV2/containers/chatbotList.container')
);

const ChatbotV2 = lazy(() =>
	import('../ChatbotV2/containers/chatbot.container')
);
const ChatbotQuestions = lazy(() =>
	import('../ChatbotV2/containers/addOrEditQuestions.container')
);
const ChatbotDetails = lazy(() => import('../Chatbot/ChatbotDetails'));
const ChatbotRequest = lazy(() => import('../Chatbot/ChatbotRequest'));

const ChatbotCreate = lazy(() =>
	import('../ChatbotV2/containers/chatbotCreate.container')
);

const Conversation = lazy(() => import('../conversation/Conversation'));
// const Dashboard = lazy(() => import('../landing/Dashboard'));
const DashboardNew = lazy(() => import('../landing/containers/dashboardNew'));
const Developers = lazy(() => import('../Developers/Developers'));
const Directory = lazy(() => import('../Directory'));
const SMS = lazy(() => import('../channels/SMS'));
const Fallback = lazy(() => import('../AnalyticsBeta/Dashboard/fallback/fallbackTab'));
const Email = lazy(() => import('../channels/Email'));
const Video = lazy(() => import('../channels/Video'));
const SettingsV2 = lazy(() => import('../SettingsV2/SettingsV2'));
const AnalyticsBeta = lazy(()=> import('../AnalyticsBeta/AnalyticsBeta'))
const CallBackProfile = lazy(() =>
	import('../CallbackProfiles/containers/CallbackProfile')
);

// const FlowBuilder = lazy(() => import('../flowBuilder/Dashboard'));
const FlowBuilderFlows = lazy(() => import('../flowBuilder/ExistingFlows'));
const StepwiseLogs = lazy(() =>
	import('../flowBuilder/StepwiseLogs/containers/StepwiseLogs')
);
const RbmRequest = lazy(() => import('../RBM/RbmRequest'));
const WhatsappPlans = lazy(() =>
	import('../channels/whatsappPlans/WhatsappPlans')
);
const VoiceCampaigns = lazy(() =>
	import('../Campaigns/Voice/VoiceCampaignTabs')
);
const WhatsappCampaignRoutes = lazy(() =>
	import('../whatsappCampaign/CampaignRoutes/WhatsappCampaignTabs')
);
const NumbersManage = lazy(() => import('../Numbers/Manage/NumbersManage'));
const Billing = lazy(() => import('../Billing/Billing'));

const FlowBuilderTabs = lazy(() => import('../flowBuilder/FlowBuilderTabs'));
const Whatsapp = lazy(() => import('../channels/Whatsapp'));
const SubscribeNumber = lazy(() =>
	import('../Numbers/Subscribe/SubscribeNumber')
);
const WhatsappActivation = lazy(() =>
	import('../Directory/organisms/activation')
);
const urlShortener = lazy(() => import('../urlShortener'));
// const failover = lazy(() => import('../failover'));
// TODO: Change when using KDS sidebar
const FallbackSideBar = lazy(() => import('../Fallback/index'));

const urlShortenerBulkDetails = lazy(() =>
	import('../urlShortener/containers/bulkUrlShortneDetails')
);
const smsDashboard = lazy(() =>
	import('../channels/SMS/organisms/campaigns/index')
);
const verify = lazy(() => import('../Verify/Verify'));
const conversation = lazy(() => import('../conversation/Conversation'));

// Added for testing integration directory
const IntegrationDirectory = lazy(() => import('../IntegrationDirectory'));

const WaCampaign = lazy(() =>
	import('../whatsappCampaign/containers/launchCampaign.container')
);
const WaCampaignDetail = lazy(() =>
	import('../whatsappCampaign/CampaignDetail/CampaignDetail.container')
);
const chatbotTab = lazy(() => import('../ChatbotV2/chatbotTabs'));
const callbacksTab = lazy(() => import('../CallbackProfiles/callbacksTab'));

type FullAppPropsType = {
	kycStatus: KYCStatus;
	doesExist: boolean;
	emailId: string;
};
type DispatchPropsType = {
	backToParentAccount: (data: any, callBack: any) => void;
	onLoad: () => void;
	requestLogout: () => void;
};
const FullApp: React.FC<FullAppPropsType & DispatchPropsType> = ({
	kycStatus,
	doesExist,
	emailId,
	backToParentAccount,
	onLoad,
	requestLogout,
}) => {
	const [tabs, setTabs] = React.useState<
		Array<Omit<TabItemPropsType, 'onClick'>>
	>([]);

	const [sidebarMenu, setSidebarMenu] = React.useState<SidebarMenuProps>([]);
	const { t } = useTranslation();

	React.useEffect(() => {
		// check for flow-builder view permissions
		// if present do nothing
		// else redirect to dashboard
		const closedUrls = [
			'/flow-builder',
			'/flow-builder/existing-flows',
			'/flow-builder/templates',
		];
		const numbersSubscribeUrl = ['/numbers/subscribe'];
		const numbersManageUrl = ['/numbers/manage'];
		const addSubaccountUrl = ['/add-subaccount'];
		const rbmActivation = ['/channels/RBM-activation-request'];

		if (closedUrls.includes(window.location.pathname)) {
			if (
				!checkPermissions('FLOWBUILDER_VIEW') &&
				checkEnabledChannel(['voice'])
			) {
				redirectToDashboard();
			}
		}
		if (['/settings/callback-profiles'].includes(window.location.pathname)) {
			window.location.href = window.location.origin + '/callbacks'
		}

		if (numbersSubscribeUrl.includes(window.location.pathname)) {
			if (!checkPrivileges('numbers', 'subscribe') || !checkEnabledChannel(['voice', 'sms'])) {
				redirectToDashboard();
			}
		}
		if (numbersManageUrl.includes(window.location.pathname)) {
			if (!checkPrivileges('numbers', 'manage') || !checkEnabledChannel(['voice', 'sms'])) {
				redirectToDashboard();
			}
		}
		if (fallbackUrl.includes(window.location.pathname)) {
			if (
				// !checkFailoverAccess() ||
				!checkPrivileges('fallback', 'overview')
				// !checkEnabledChannel(['sms'])
			) {
				redirectToDashboard();
			}
		}
		if (receiveMessageUrl.includes(window.location.pathname)) {
			if (!checkPrivileges('channels', 'receive_message')) {
				redirectToDashboard();
			}
		}
		if (window.location.pathname.includes('/chatbot')) {
			if (
				!checkPrivileges('chatbot', 'main') ||
				!checkChatbotPrivileges()
			) {
				redirectToDashboard();
			}
			else if (window.location.pathname.includes('/chatbot/overview')) {
				if (!checkPrivileges('chatbot', 'overview')) {
					redirectToDashboard();
				}
			}
			else if (window.location.pathname.includes('/chatbot/report')) {
				if (!checkPrivileges('chatbot', 'reports')) {
					redirectToDashboard();
				}
			}
		}
		if (whatsappCampaignUrls.includes(window.location.pathname)) {
			if (!checkEnabledChannel(['whatsapp'])) {
				redirectToDashboard();
			}
		}
		if (whatsappCampaignLaunchUrls.includes(window.location.pathname)) {
			if (!checkPrivileges('campaigns', 'campaign_launch')) {
				window.location.href = '/campaigns/whatsapp';
			}
		}
		if (voiceCampaignLaunchUrls.includes(window.location.pathname)) {
			if (!checkPrivileges('campaigns', 'campaign_launch')) {
				window.location.href = '/campaigns/voice';
			}
		}
		if (integrationDirectoryUrl.includes(window.location.pathname)) {
			if (!checkPrivileges('integration_directory', 'main')) {
				redirectToDashboard();
			}
		}
		if (dashboardUrls.includes(window.location.pathname)) {
			if (
				!checkPrivileges('dashboard', 'main') ||
				!checkEnabledChannel(['sms', 'voice'])
			) {
				redirectToDashboard();
			}
		}
		if (channelsUrls.includes(window.location.pathname)) {
			if (!checkPrivileges('channels', 'main')) {
				redirectToDashboard();
			}
		}
		if (campaignsUrls.includes(window.location.pathname)) {
			if (!checkPrivileges('campaigns', 'main')) {
				redirectToDashboard();
			}
		}
		if (addSubaccountUrl.includes(window.location.pathname)) {
			if (
				!checkPrivileges('subaccount', 'main') ||
				checkViewOnlyPrivileges('subaccounts', 'manage')
			) {
				redirectToDashboard();
			}
		}
		if (conversationUrls.includes(window.location.pathname)) {
			if (!checkIsConversationEnabled()) {
				redirectToDashboard();
			}
		}

		if (developersUrl.includes(window.location.pathname)) {
			if (checkForSpecificCompanyId('child')) {
				redirectToDashboard();
			}
		}
		if (rbmActivation.includes(window.location.pathname)) {
			if (
				checkPrivileges('view_only', 'main') ||
				checkWhatsappOnlyRole() ||
				checkEnabledChannel(['voice'])
			) {
				redirectToDashboard();
			}
		}
		if (
			smsUrls.includes(window.location.pathname) ||
			voiceUrls.includes(window.location.pathname)
		) {
			if (checkWhatsappOnlyRole()) {
				redirectToDashboard();
			}
		}
		if (window.location.pathname.includes('/chatbot')) {
			if (
				kycStatus !== KYCStatus.approved &&
				kycStatus !== KYCStatus.autoApproved
			) {
				window.location.href = REDIRECT_URL + `/billing/preferences`;
			}
		}
		if (
			HIDE_FEATURE_URLS &&
			HIDE_FEATURE_URLS.split(',').includes(window.location.pathname)
		) {
			redirectToDashboard();
		}

		if (['/callback-profiles'].includes(window.location.pathname)) {
			if (!checkPrivileges('settings', 'callback_profiles')) {
				redirectToDashboard();
			}
		}

		if (window.location.pathname.includes(urls.v2Whatsapp({}))) {
			if (!isActiveWhatsappAccount()) {
				redirectToDashboard();
			}
		}
	}, []);

	const { isKDSPage, pageName } = useSelector(
		(state: IStateType) => state.kdsView
	);

	useEffect(() => {
		var element: any = document.getElementById('page-top');
		var root: any = document.getElementById('root');
		var modal: any = document.getElementById('root-modal');

		element.style.fontFamily = isKDSPage
			? 'Work Sans'
			: 'Heebo, sans-serif';
		root.style.fontFamily = isKDSPage ? 'Work Sans' : 'Heebo, sans-serif';
		modal.style.fontFamily = isKDSPage ? 'Work Sans' : 'Heebo, sans-serif';
	}, [isKDSPage, window.location.href]);

	const externalPage = (pageName: string): JSX.Element => {
		return <PageContent pageName={pageName} />;
	};

	const IntegrationDirectoryComponent = () => {
		if (doesExist) {
			return IntegrationDirectory;
		}
		return ErrorComponent;
	};

	const CampaignRouteComponent = React.useCallback(
		() => addTabFN(CampaignRoute, setTabs),
		[CampaignRoute, setTabs]
	);

	const CampaignsComponent = () => {
		if (doesExist) {
			return CampaignRouteComponent;
		}
		return ErrorComponent;
	};

	function addTabFN(
		// using any, else need to import props from the component.
		Component: any,
		setTabsFN: (tabs: Array<Omit<TabItemPropsType, 'onClick'>>) => void,
		params?: Record<string, any>
	) {
		return <Component setTabs={setTabsFN} params={params} />;
	}

	const addSidebarMenu = (
		Component: React.LazyExoticComponent<
			(props: {
				setSidebarMenu: (sidebarMenu: SidebarMenuProps) => void;
			}) => JSX.Element
		>,
		setSidebarMenuCallback: (sidebarMenu: SidebarMenuProps) => void
	) => {
		return <Component setSidebarMenu={setSidebarMenuCallback} />;
	};

	const BillingComponent = React.useCallback(
		() => addTabFN(Billing, setTabs),
		[Billing, setTabs]
	);
	const DirectoryComponent = React.useCallback(
		() => addTabFN(Directory, setTabs),
		[Directory, setTabs]
	);

	const WhatsappComponent = React.useCallback(
		() => addTabFN(WhatsappCampaignRoutes, setTabs),
		[WhatsappCampaignRoutes, setTabs]
	);

	const SMSComponent = React.useCallback(() => addTabFN(SMS, setTabs), [
		SMS,
		setTabs,
	]);
	const EmailComponent = React.useCallback(
		() => addSidebarMenu(Email, setSidebarMenu),
		[Email, setSidebarMenu]
	);

	const VideoComponent = React.useCallback(
		() => addSidebarMenu(Video, setSidebarMenu),
		[Video, setSidebarMenu]
	);
	const SettingsV2Component = React.useCallback(
		() => addSidebarMenu(SettingsV2, setSidebarMenu),
		[SettingsV2, setSidebarMenu]
	);
	// TODO: Change when using KDS Sidebar
	const FallbackComponent = React.useCallback(
		() => addSidebarMenu(FallbackSideBar, setSidebarMenu),
		[FallbackSideBar, setSidebarMenu]
	);

	const AnalyticsBetaCompnent = React.useCallback(
		() => addSidebarMenu(AnalyticsBeta,setSidebarMenu),
		[AnalyticsBeta,setSidebarMenu]
	)

	const UrlShortenerComponent = React.useCallback(
		() => addTabFN(urlShortener, setTabs),
		[urlShortener, setTabs]
	);

	const VoiceCampaignsComponent = React.useCallback(
		() => addTabFN(VoiceCampaigns, setTabs, { emailId }),
		[VoiceCampaigns, setTabs]
	);

	const WhatsappCampaignsComponent = React.useCallback(
		() => addTabFN(WhatsappCampaignRoutes, setTabs, { emailId }),
		[WhatsappCampaignRoutes, setTabs]
	);

	const FlowBuilderComponent = React.useCallback(
		() => addTabFN(FlowBuilderTabs, setTabs),
		[FlowBuilderTabs, setTabs]
	);
	const subAccountsManagementComponent = React.useCallback(
		() => addTabFN(subAccountsManagementTab, setTabs),
		[subAccountsManagementTab, setTabs]
	);
	const smsDashboardTab = React.useCallback(
		() => addTabFN(smsDashboard, setTabs),
		[smsDashboard, setTabs]
	);
	const verifyTab = React.useCallback(() => addTabFN(verify, setTabs), [
		verify,
		setTabs,
	]);
	const conversationTab = React.useCallback(
		() => addTabFN(conversation, setTabs),
		[conversation, setTabs]
	);
	const chatBotTab = React.useCallback(() => addTabFN(chatbotTab, setTabs), [
		chatbotTab,
		setTabs,
	]);

	const callBacksTab = React.useCallback(() => addTabFN(callbacksTab, setTabs), [
		callbacksTab,
		setTabs,
	]);

	const developerTab = React.useCallback(
		() => addTabFN(Developers, setTabs),
		[Developers, setTabs]
	);

	const secondaryConversation = () => {
		return (
			<EmptyState
				bordered
				headMsg={t('KLR02879')}
				subHeadMsg={
					<>
						<div>{t('KLR02887')}</div>
						<div>{t('KLR02880')}</div>
						<div>{t('KLR02881')}</div>
					</>
				}
			/>
		)
	};

	return (
		<Router>
			<AppSetup
				kycStatus={kycStatus}
				tabs={tabs}
				sidebarMenu={sidebarMenu}
				backToParentAccount={backToParentAccount}
				onLoad={onLoad}
				requestLogout={requestLogout}
			>
				<div className={'containerHome'}>
					<Suspense
						fallback={<Loader marginTop={250} barWidth={500} />}
					>
						<Switch>
							<Route
								path={urls.v1Developers()}
								component={developerTab}
							/>
							<Route
								exact
								path={urls.flowBuilder({
									route: 'existing-flows',
								})}
								component={FlowBuilderFlows}
							/>
							<Route
								exact
								path={urls.flowBuilder({ route: 'templates' })}
								component={FlowBuilderFlows}
							/>
							<Route
								exact
								path={'/flow-builder/logs/:id'}
								component={StepwiseLogs}
							/>
							<Route
								exact
								path={'/channels/voice/sip-user-group/:group'}
								component={SipCredential}
							/>
							<Route
								exact
								path={'/channels/voice/sip-acl/:acl'}
								component={SipIP}
							/>
							{/* <Route
								exact
								path={urls.flowBuilder({})}
								component={FlowBuilder}
							/> */}
							<Route
								path={urls.dashboard()}
								component={DashboardNew}
							/>
							<Route
								path={urls.conversations()}
								component={IS_SECONDARY_REGION ? secondaryConversation : conversationTab}
							/>
							<Route
								path={urls.chatBot({ page: 'request-form' })}
								component={ChatbotRequest}
								exact
							/>
							<Route
								path={urls.chatBot({ page: 'bot-details' })}
								component={ChatbotDetails}
								exact
							/>
							<Route
								path={urls.chatBot({ page: ':botname' })}
								component={ChatbotV2}
								exact
							/>
							<Route
								path={urls.chatBot({
									page: ':botname/add-question',
								})}
								component={ChatbotQuestions}
								exact
							/>
							<Route
								path={urls.chatBot({
									page: ':botname/edit-question',
								})}
								component={ChatbotQuestions}
								exact
							/>

							<Route
								path={urls.chatBot({
									page: ':type/create',
								})}
								component={ChatbotCreate}
								exact
							/>

							<Route
								path={urls.ChatbotV2({})}
								component={chatBotTab}
							/>

							<Route
								path={urls.CallbacksV2({})}
								component={callBacksTab}
							/>

							<Route
								path={urls.channels({
									page: 'RBM-activation-request',
								})}
								component={RbmRequest}
								exact
							/>
							<Route
								path={urls.channels({})}
								component={ChannelsList}
								exact
							/>
							<Route
								exact
								path={'/campaigns/whatsapp/launch'}
								component={WaCampaign}
							/>
							<Route
								exact
								path={urls.v2WhatsappCampaign({})}
								component={WaCampaignDetail}
							/>
							<Route
								exact
								path={urls.v1Campaign()}
								component={WhatsappCampaignsComponent}
							/>
							<Route
								path={urls.campaigns()}
								component={CampaignsComponent()}
							/>

							{/* newly added sound manager page */}

							<Route
								path={urls.v1CampaignManager()}
								component={VoiceCampaignsComponent}
							/>
							<Route
								path={urls.v1SoundManager()}
								component={VoiceCampaignsComponent}
							/>
							<Route
								path={urls.v1NumbersManage()}
								component={NumbersManage}
							/>

							<Route path={urls.verify()} component={verifyTab} />
							<Route
								path={urls.v1Directory()}
								component={DirectoryComponent}
							/>
							<Route
								path={urls.v1Billing()}
								component={BillingComponent}
							/>
							<Route
								path={urls.v2Whatsapp()}
								component={WhatsappComponent}
							/>
							<Route
								path={urls.v1WhatsappActivation()}
								component={WhatsappActivation}
							/>
							<Route
								path={urls.v1SMS()}
								component={SMSComponent}
							/>
							<Route
								path={urls.v1Email()}
								component={EmailComponent}
							/>
							<Route
								path={urls.getVideoPath()}
								component={VideoComponent}
							/>
							<Route
								path={urls.v2Settings()}
								component={SettingsV2Component}
							/>
							<Route
								path={urls.AnalyticsBeta()}
								component={AnalyticsBetaCompnent}
							/>
							<Route
								path={urls.v1SubscribeNumber()}
								component={SubscribeNumber}
							/>
							<Route path={urls.billing()}>
								{externalPage('Billing')}
							</Route>
							<Route path={urls.directory()}>
								<PageContent pageName="Directory" />
							</Route>
							<Route path={urls.numbers()}>
								<PageContent pageName="Numbers" />
							</Route>
							<Route
								path={urls.v1flowBuilder()}
								component={FlowBuilderComponent}
							/>

							{/* Disabling Integration Directory Route CNP-1828 */}
							{/* <Route
								path={urls.integrationDirectory()}
								component={IntegrationDirectoryComponent()}
							/> */}
							<Route
								path={'/URL-shortener/bulk-upload/details'}
								component={urlShortenerBulkDetails}
							/>
							<Route
								path={urls.urlShortener()}
								component={UrlShortenerComponent}
							/>
							<Route
								path={urls.subAccountsManagement()}
								component={subAccountsManagementComponent}
							/>
							<Route
								path={urls.addSubAccounts()}
								component={AddingSubAccounts}
							></Route>
							<Route
								path={urls.editSubAccounts()}
								component={EditSubAccount}
							></Route>
							
							{/* TODO: Change when using KDS sidebar */}
							<Route
								path={urls.fallback()}
								component={FallbackComponent}
							/>
							<Route
								path={urls.v1SMSDashboard()}
								component={smsDashboardTab}
							/>
							<Route
								render={() => (
									<Redirect exact to={defaultRedirectTo()} />
								)}
							/>
						</Switch>
					</Suspense>
				</div>
			</AppSetup>
			<RootModal />
			<RootSideBar />
		</Router>
	);
};

export default FullApp;